import { useState, useMemo, Fragment } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, ChevronDown, ChevronRight, CheckCircle2, Trash2, Bug, RefreshCw } from 'lucide-react';
import { api } from '@/api/client';

interface ErrRow {
  id: string; statusCode: number; method: string; path: string; message: string; stack?: string | null;
  body?: string | null; name?: string | null; actorType?: string | null; actorId?: number | null;
  actorName?: string | null; ip?: string | null; resolved: boolean; createdAt: string;
}
interface Resp { total: number; page: number; pageSize: number; unresolved: number; items: ErrRow[]; }

export function ErrorLogPanel() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [resolved, setResolved] = useState('false'); // default: show open errors
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const pageSize = 50;

  const params = useMemo(() => {
    const p: any = { page, pageSize };
    if (search) p.search = search;
    if (resolved) p.resolved = resolved;
    return p;
  }, [search, resolved, page]);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['error-log', params],
    queryFn: async () => (await api.get<Resp>('/error-log', { params })).data,
    refetchInterval: 30000,
  });

  const resolveMut = useMutation({
    mutationFn: async (v: { id: string; resolved: boolean }) => (await api.patch(`/error-log/${v.id}/resolve`, { resolved: v.resolved })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['error-log'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });
  const delMut = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/error-log/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['error-log'] }); toast.success('Deleted'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });

  const total = data?.total ?? 0;
  const items = data?.items ?? [];
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><Bug size={18} /> Error Log</h2>
          <p className="text-xs text-slate-500 mt-0.5">Server (5xx) errors captured automatically — expand a row for the full detail to share.</p>
        </div>
        <button onClick={() => refetch()} className="text-sm border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50 flex items-center gap-1.5">
          <RefreshCw size={13} className={isFetching ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Kpi icon={<AlertTriangle className="text-rose-600" />} label="Open errors" value={data?.unresolved ?? 0} tone="rose" />
        <Kpi icon={<Bug className="text-slate-600" />} label="Shown" value={total} />
        <Kpi icon={<CheckCircle2 className="text-emerald-600" />} label="View" value={0} hideValue sub="Filter below" />
      </div>

      <div className="grid grid-cols-4 gap-3">
        <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search path / message / user..." className="border border-slate-300 rounded px-2 py-1.5 text-sm col-span-2" />
        <select value={resolved} onChange={(e) => { setResolved(e.target.value); setPage(1); }} className="border border-slate-300 rounded px-2 py-1.5 text-sm">
          <option value="false">Open (unresolved)</option>
          <option value="true">Resolved</option>
          <option value="">All</option>
        </select>
        <button onClick={() => { setSearch(''); setResolved('false'); setPage(1); }} className="text-sm border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50">Reset</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-600">
            <tr>
              <th className="w-8 px-2 py-2"></th>
              <th className="text-left px-3 py-2">When</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Where</th>
              <th className="text-left px-3 py-2">Message</th>
              <th className="text-left px-3 py-2">User</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="text-center py-6 text-slate-400">Loading…</td></tr>}
            {!isLoading && items.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-emerald-600">🎉 No errors here.</td></tr>}
            {items.map(it => (
              <Fragment key={it.id}>
                <tr className={`border-t hover:bg-slate-50 ${it.resolved ? 'opacity-60' : ''}`}>
                  <td className="px-2 py-2 text-center">
                    <button onClick={() => setOpen(o => ({ ...o, [it.id]: !o[it.id] }))} className="text-slate-400 hover:text-slate-700">
                      {open[it.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{new Date(it.createdAt).toLocaleString('en-IN')}</td>
                  <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 rounded border bg-rose-50 text-rose-700 border-rose-200 font-mono">{it.statusCode}</span></td>
                  <td className="px-3 py-2 text-xs"><span className="font-mono text-slate-500">{it.method}</span> <span className="font-mono text-slate-800 break-all">{it.path}</span></td>
                  <td className="px-3 py-2 text-xs text-slate-800 max-w-[280px] truncate" title={it.message}>{it.name && <span className="text-rose-600 font-mono mr-1">{it.name}</span>}{it.message}</td>
                  <td className="px-3 py-2 text-xs">{it.actorName ?? (it.actorType ? `${it.actorType} ${it.actorId ?? ''}` : '—')}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => resolveMut.mutate({ id: it.id, resolved: !it.resolved })}
                      className={`text-xs px-2 py-1 rounded border mr-1 ${it.resolved ? 'text-slate-500 border-slate-200 hover:bg-slate-50' : 'text-emerald-700 border-emerald-200 hover:bg-emerald-50'}`}>
                      {it.resolved ? 'Reopen' : 'Resolve'}
                    </button>
                    <button onClick={() => { if (confirm('Delete this error entry?')) delMut.mutate(it.id); }} className="text-xs p-1 text-slate-400 hover:text-rose-600" title="Delete"><Trash2 size={13} /></button>
                  </td>
                </tr>
                {open[it.id] && (
                  <tr className="bg-slate-50/70">
                    <td></td>
                    <td colSpan={6} className="px-3 py-3">
                      <div className="space-y-2 text-xs">
                        <div><span className="font-semibold text-slate-600">Message:</span> <span className="text-slate-800">{it.message}</span></div>
                        {it.ip && <div><span className="font-semibold text-slate-600">IP:</span> <span className="font-mono">{it.ip}</span></div>}
                        {it.body && <div><span className="font-semibold text-slate-600">Request body:</span><pre className="mt-1 bg-white border border-slate-200 rounded p-2 overflow-x-auto text-[11px] whitespace-pre-wrap break-all">{it.body}</pre></div>}
                        {it.stack && <div><span className="font-semibold text-slate-600">Stack:</span><pre className="mt-1 bg-slate-900 text-slate-100 rounded p-2 overflow-x-auto text-[11px] max-h-64">{it.stack}</pre></div>}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between text-sm">
        <div className="text-slate-500">Page {page} of {lastPage} · {total} total</div>
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1 border rounded disabled:opacity-30">Prev</button>
          <button disabled={page >= lastPage} onClick={() => setPage(page + 1)} className="px-3 py-1 border rounded disabled:opacity-30">Next</button>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, sub, tone = 'slate', hideValue }: { icon: React.ReactNode; label: string; value: number; sub?: string; tone?: string; hideValue?: boolean }) {
  const bg = tone === 'rose' ? 'bg-rose-50 border-rose-200' : 'bg-white border-slate-200';
  return (
    <div className={`border rounded-lg p-3 ${bg}`}>
      <div className="flex items-center gap-2 text-xs text-slate-600 mb-1">{icon} {label}</div>
      {!hideValue && <div className="text-2xl font-semibold">{value}</div>}
      {sub && <div className="text-[10px] text-slate-400">{sub}</div>}
    </div>
  );
}
